import { useQuery } from '@tanstack/react-query';
import { assembleInvoiceDocument } from '@releve/shared/documents/invoice/assemble';
import type { InvoiceType } from '@releve/shared/documents/invoice/model';
import type { DocumentLanguage } from '@releve/shared/format/locale';
import { supabase } from '@/integrations/supabase/client';

/**
 * The assembled invoice document, for on-screen rendering of the shared
 * `InvoiceDocument` template.
 *
 * This is the SAME assembler the PDF endpoint runs
 * (`POST /api/v1/pdf/orgs/:orgId/invoice`) — the only differences are the
 * client it is handed (the browser's RLS-scoped singleton instead of the API's
 * per-request one) and `inlineLogo: false`, because a preview can resolve the
 * logo's storage path to a signed URL and does not need a fat base64 payload in
 * the query cache. Screen and PDF therefore print the same numbers by
 * construction, not by convention.
 */

/** Languages documents exist in; anything else falls back to English. */
const DOCUMENT_LANGUAGES = new Set<string>(['en', 'fr', 'nl']);

/**
 * Narrow an i18next language (`'nl'`, `'nl-BE'`, a stale localStorage value) to
 * a language the documents package has resources for. Mirrors
 * `localeForLanguage`'s primary-subtag rule, one level up: that maps language →
 * number locale, this maps language → document language.
 */
export function normalizeDocumentLanguage(language: string | undefined): DocumentLanguage {
  const primary = (language ?? 'en').slice(0, 2).toLowerCase();
  return DOCUMENT_LANGUAGES.has(primary) ? (primary as DocumentLanguage) : 'en';
}

export interface UseInvoiceDocumentParams {
  organizationId?: string;
  invoiceType: InvoiceType;
  invoiceId?: string;
  /** Raw `i18n.language`; normalized here so callers don't have to. */
  language: string | undefined;
}

export const invoiceDocumentQueryKey = ({
  organizationId,
  invoiceType,
  invoiceId,
  language,
}: UseInvoiceDocumentParams) =>
  [
    'invoice-document',
    organizationId,
    invoiceType,
    invoiceId,
    normalizeDocumentLanguage(language),
  ] as const;

export function useInvoiceDocument(params: UseInvoiceDocumentParams) {
  const { organizationId, invoiceType, invoiceId, language } = params;

  return useQuery({
    queryKey: invoiceDocumentQueryKey(params),
    queryFn: async () => {
      if (!organizationId || !invoiceId) return null;
      return assembleInvoiceDocument(supabase, {
        orgId: organizationId,
        invoiceType,
        invoiceId,
        // The preview resolves `org.logo.storagePath` through `useSignedUrl`;
        // only the headless renderer needs the inlined data URI.
        inlineLogo: false,
        language: normalizeDocumentLanguage(language),
        now: new Date(),
      });
    },
    enabled: !!organizationId && !!invoiceId,
  });
}
